import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate
} from "react-router-dom";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import ProfileSetupPage from "./pages/ProfileSetupPage";
import GoogleCallbackHandler from "./pages/GoogleCallbackHandler";
import ContactFormPage from "./pages/ContactFormPage";
import ConfirmEmailPage from "./pages/ConfirmEmailPage";
import ProfilePage from "./pages/ProfilePage";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import YourCrewPage from "./pages/YourCrewPage";
import LoggedOutPage from "./pages/LoggedOutPage";
import RidesPage from "./pages/RidesPage";
import GearPage from "./pages/GearPage";
import CreateTripPage from "./pages/CreateTrip";
import AddLocation from "./pages/AddLocation";
import CreateTripDetailsPage from "./pages/CreateTripDetailsPage";
import TripPage from "./pages/TripPage"; // ✅ NEW
import "./pages/LoginPage.css";
import { Loader } from "@googlemaps/js-api-loader";
import TripAttendancePage from "./pages/TripAttendancePage"; // ✅ NEW


const loader = new Loader({
  apiKey: "AIzaSyDouCFVON6WJwswrrNSIqM_VR1xQr0UCRs",
  version: "weekly",
  libraries: ["places"],
});

loader.load().then(() => {
  console.log("✅ Google Maps API loaded");
});

function AppInner() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [hasProfile, setHasProfile] = useState(null);
  const [profileChecked, setProfileChecked] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/auth/me", {
      credentials: "include",
      headers: {
        "Cache-Control": "no-store",
        "Pragma": "no-cache"
      }
    })
      .then((res) => res.ok ? res.json() : Promise.resolve({ user: null }))
      .then((data) => {
        console.log("📥 /auth/me response:", data);
        setCurrentUser(data.user);
        setAuthChecked(true);
      })
      .catch((err) => {
        console.log("❌ /auth/me error:", err);
        setCurrentUser(null);
        setAuthChecked(true);
      });
  }, []);

  useEffect(() => {
    if (authChecked && currentUser && hasProfile === null) {
      fetch("/api/profile", { credentials: "include" })
        .then(async (res) => {
          console.log("📥 /profile status:", res.status);
          const data = await res.json();

          if (res.status === 404 || res.status === 400) {
            setHasProfile(false);
          } else if (res.ok) {
            setHasProfile(true);
          }
        })
        .catch((err) => {
          console.log("❌ /profile error:", err);
          setHasProfile(false);
        })
        .finally(() => {
          setProfileChecked(true);
        });
    } else if (authChecked && !currentUser) {
      setProfileChecked(true);
    }
  }, [authChecked, currentUser, hasProfile]);

  useEffect(() => {
    console.log("🧭 Checking redirect with state:", {
      authChecked,
      profileChecked,
      currentUser,
      hasProfile,
      path: location.pathname
    });

    const publicRoutes = ["/login", "/forgot-password", "/reset-password", "/contact", "/confirm-email"];

    if (
      authChecked &&
      profileChecked &&
      !currentUser &&
      !publicRoutes.includes(location.pathname)
    ) {
      navigate("/login", { replace: true });
    }

    if (
      authChecked &&
      profileChecked &&
      currentUser &&
      hasProfile === false &&
      location.pathname !== "/complete-profile" &&
      location.pathname !== "/login"
    ) {
      console.log("🔁 Redirecting to /complete-profile");
      navigate("/complete-profile", { replace: true });
    }
  }, [authChecked, profileChecked, currentUser, hasProfile, location.pathname, navigate]);

  useEffect(() => {
    console.log("🔍 App render state:", {
      pathname: location.pathname,
      currentUser,
      authChecked,
      hasProfile,
      profileChecked
    });
  }, [location.pathname, currentUser, authChecked, hasProfile, profileChecked]);

  if (!authChecked || !profileChecked) {
    return <div style={{ padding: "40px", textAlign: "center" }}>Checking session…</div>;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#2c7373",
        color: "white",
      }}
    >
      <Routes>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage setCurrentUser={setCurrentUser} />} />
        <Route path="/login" element={<LoginPage setCurrentUser={setCurrentUser} />} />
        <Route path="/profile-setup" element={<ProfileSetupPage setHasProfile={setHasProfile} user={currentUser} />} />
        <Route path="/complete-profile" element={<CompleteProfilePage user={currentUser} setHasProfile={setHasProfile} />} />
        <Route path="/" element={<HomePage currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
        <Route path="/post-auth" element={<GoogleCallbackHandler setCurrentUser={setCurrentUser} />} />
        <Route path="/contact" element={<ContactFormPage />} />
        <Route path="/confirm-email" element={<ConfirmEmailPage />} />
        <Route path="/profile" element={<ProfilePage currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
        <Route path="/your-crew" element={<YourCrewPage user={currentUser} currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
        <Route path="/logged-out" element={<LoggedOutPage />} />
        <Route path="/your-rides" element={<RidesPage currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
        <Route path="/your-gear" element={<GearPage currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
        <Route path="/createtrip" element={<CreateTripPage currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
        <Route path="/add-location" element={<AddLocation currentUser={currentUser} setCurrentUser={setCurrentUser} />} />
        <Route path="/create-trip-details" element={<CreateTripDetailsPage />} />
        <Route path="/trip/:tripId" element={<TripPage currentUser={currentUser} />} /> {/* ✅ NEW */}
        <Route path="/trip/:tripId/attendance" element={<TripAttendancePage />} /> {/* ✅ NEW */}
      </Routes>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppInner />
    </Router>
  );
}
